import { SeriesList, Module } from './../models';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/internal/operators/map';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';

@Injectable({
  providedIn: 'root'
})
export class SeriesService {

  public moduleSubject: BehaviorSubject<Module[]>;
  public seriesSubject: BehaviorSubject<SeriesList[]>;
  modules: Module[] = [];
  series: SeriesList[] = [];

  constructor(
    private http: HttpClient,
    private authService: AuthService) {
      this.moduleSubject = new BehaviorSubject<Module[]>(new Array<Module>());
      this.seriesSubject = new BehaviorSubject<SeriesList[]>(new Array<SeriesList>());
  }

  public get moduleValue(): Module[] {
    return this.moduleSubject.value;
  }

  public get seriesValue(): SeriesList[] {
    return this.seriesSubject.value;
  }

  getSeries() {
    return this.http.get<any>(`${environment.apiUrl}/series`)
      .pipe(map(response => {
        if (response.success && response.data.series.length > 0) {
          this.setSeriesData(response.data.series);
        }
        this.authService.setNewToken(response.token);
        this.authService.setUserData(response.user);
        return response;
      }));
  }

  getSeriesAdminDashboard() {
    return this.http.get<any>(`${environment.apiUrl}/admin/series/list`)
      .pipe(map(response => {
        if (response.success && response.data.series.length > 0) {
          this.setSeriesData(response.data.series);
        }
        this.authService.setNewToken(response.token);
        this.authService.setUserData(response.user);
        return response;
      }));
  }

  getSeriesAdmin() {
    return this.http.get<any>(`${environment.apiUrl}/admin/series`)
      .pipe(map(response => {
        this.authService.setNewToken(response.token);
        this.authService.setUserData(response.user);
        return response;
      }));
  }

  addSeries(seriesName: string) {
    return this.http.post<any>(`${environment.apiUrl}/admin/series`, {name: seriesName})
      .pipe(map(response => {
        this.authService.setNewToken(response.token);
        this.authService.setUserData(response.user);
        return response;
      }));
  }

  updateSeries(seriesId: number, seriesName: string = '', sequence: number = 0, status: string = 'Draft') {
    const formData: any = {};

    if (seriesName !== '') {
      formData.name = seriesName;
    }

    if (sequence > 0) {
      formData.sequence = sequence;
    }

    formData.status = status;

    return this.http.post<any>(`${environment.apiUrl}/admin/series/save/${seriesId}`, formData)
      .pipe(map(response => {
        this.authService.setNewToken(response.token);
        this.authService.setUserData(response.user);
        return response;
      }));
  }

  getSeriesModules(seriesId: number) {
    return this.http.get<any>(`${environment.apiUrl}/series/${seriesId}`)
      .pipe(map(response => {
        if (response.success && response.data.series.modules.length > 0) {
          this.setModuleData(response.data.series.modules);
        }
        this.authService.setNewToken(response.token);
        this.authService.setUserData(response.user);
        return response;
      }));
  }

  getSeriesModulesAdminDashboard(seriesId: number) {
    return this.http.get<any>(`${environment.apiUrl}/admin/series/${seriesId}`)
      .pipe(map(response => {
        if (response.success && response.data.series.modules.length > 0) {
          this.setModuleData(response.data.series.modules);
        }
        this.authService.setNewToken(response.token);
        this.authService.setUserData(response.user);
        return response;
      }));
  }

  getSeriesModulesAdmin(seriesId: number) {
    return this.http.get<any>(`${environment.apiUrl}/admin/series/get/${seriesId}`)
      .pipe(map(response => {
        this.authService.setNewToken(response.token);
        this.authService.setUserData(response.user);
        return response;
      }));
  }

  addSeriesModule(seriesId: number, details: FormData) {
    return this.http.post<any>(`${environment.apiUrl}/admin/series/save_module/${seriesId}`, details)
      .pipe(map(response => {
        this.authService.setNewToken(response.token);
        this.authService.setUserData(response.user);
        return response;
      }));
  }

  setModuleData(modules: any) {
    this.modules = [];
    modules.forEach(data => {
      this.modules.push({
        id: data.id,
        name: data.name,
        description: data.description,
        coverUrl: data.cover_url,
        videoUrl: data.video_url,
        videoDuration: data.video_duration,
        modulePoints: data.module_points,
        isWatched: data.is_watched,
        exerciseCompleted: data.exercise_completed,
        quizCompleted: data.quiz_completed,
        quiz: data.quiz,
        summary: data.summary,
        breathingExerciseUrl: data.breathing_exercise_url
      });
    });
    this.moduleSubject.next(this.modules);
  }

  setSeriesData(series: any) {
    this.series = [];
    series.forEach(data => {
      this.series.push({
        series_id: data.series_id,
        series_name: data.series_name,
        sequence_number: data.sequence_number,
        modules: data.modules
      });
    });
    this.seriesSubject.next(this.series);
  }

}
